package com.fantasytenis.web.screens.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.bracket.entity.*
import com.fantasytenis.shared.domain.bracket.usecases.FullBracketData
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderBottom
import com.fantasytenis.web.theme.h2Style
import com.fantasytenis.web.theme.h3Style
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date
import kotlin.time.Duration

/**
 * Screen showing details of a bracket challenge and allowing entry.
 */
@Composable
fun BracketChallengeDetailScreen(challengeId: String) {
    val scope = rememberCoroutineScope()
    var bracketData by remember { mutableStateOf<FullBracketData?>(null) }
    var userEntries by remember { mutableStateOf<List<BracketEntryEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var isEntering by remember { mutableStateOf(false) }

    // Load challenge data
    LaunchedEffect(challengeId) {
        isLoading = true
        error = null

        // Load full bracket data
        DependencyContainer.getFullBracketUseCase(challengeId).fold(
            onSuccess = { data ->
                bracketData = data
            },
            onFailure = { e ->
                error = e.message
            }
        )

        // Load user's entries for this challenge
        DependencyContainer.getUserBracketEntriesUseCase(challengeId).fold(
            onSuccess = { entries ->
                userEntries = entries
                isLoading = false
            },
            onFailure = { e ->
                // User might not have entries, that's OK
                isLoading = false
            }
        )
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = bracketData?.challenge?.name ?: "Challenge Details",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.BracketChallenges) },
            customActions = {
                // Leaderboard button (visible on desktop)
                Button(
                    text = "Leaderboard",
                    onClick = { NavigationState.navigate(Route.BracketLeaderboard(challengeId)) },
                    variant = "secondary"
                )
            }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                classes("challenge-main-content")
                style {
                    maxWidth(900.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                    padding(24.px, 24.px, 48.px, 24.px)
                }
            }) {
                when {
                    isLoading -> LoadingState()
                    error != null -> ErrorState(error!!) {
                        scope.launch {
                            isLoading = true
                            DependencyContainer.getFullBracketUseCase(challengeId).fold(
                                onSuccess = { bracketData = it; isLoading = false },
                                onFailure = { error = it.message; isLoading = false }
                            )
                        }
                    }
                    bracketData != null -> {
                        val challenge = bracketData!!.challenge
                        val draw = bracketData!!.draw

                        // Challenge Info Card
                        ChallengeInfoCard(challenge, draw)

                        // User's Entries Section
                        if (userEntries.isNotEmpty()) {
                            UserEntriesSection(userEntries, challengeId)
                        }

                        // Enter Challenge Section
                        if (!challenge.isLocked && !challenge.isFull) {
                            val canEnterMore = userEntries.size < challenge.maxEntriesPerUser
                            if (canEnterMore) {
                                EnterChallengeSection(
                                    challenge = challenge,
                                    isEntering = isEntering,
                                    onEnter = {
                                        scope.launch {
                                            isEntering = true
                                            DependencyContainer.createBracketEntryUseCase(
                                                challengeId = challengeId,
                                                bracketName = "My Bracket ${userEntries.size + 1}"
                                            ).fold(
                                                onSuccess = { entry ->
                                                    NavigationState.navigate(
                                                        Route.BracketBuilder(challengeId, entry.id)
                                                    )
                                                },
                                                onFailure = { e ->
                                                    error = e.message
                                                    isEntering = false
                                                }
                                            )
                                        }
                                    }
                                )
                            }
                        }

                        // Scoring Rules Section
                        ScoringRulesSection(challenge.scoringRules, draw.drawSize)

                        // Payout Structure Section
                        if (challenge.payoutStructure.isNotEmpty()) {
                            PayoutStructureSection(challenge.payoutStructure, challenge.prizePool)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun ChallengeInfoCard(
    challenge: BracketChallengeEntity,
    draw: TournamentDrawEntity
) {
    Div({
        classes("challenge-info-card")
        style {
            backgroundColor(Colors.Surface)
            borderRadius(16.px)
            padding(28.px)
            marginBottom(24.px)
            property("box-shadow", "0 4px 16px rgba(0, 0, 0, 0.08)")
            property("border-left", "4px solid rgb(0, 166, 81)")
        }
    }) {
        // Status and featured badges
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                marginBottom(16.px)
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            StatusBadge(challenge.status)
            if (challenge.isFeatured) {
                Span({
                    style {
                        backgroundColor(Colors.Secondary)
                        color(Colors.TextPrimary)
                        fontSize(11.px)
                        fontWeight(600)
                        padding(4.px, 8.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text("FEATURED")
                }
            }
        }

        // Tournament info - Hero title
        H2({
            style {
                fontSize(28.px)
                fontWeight(700)
                color(Colors.TextPrimary)
                margin(0.px)
                marginBottom(8.px)
                lineHeight("1.2")
            }
        }) {
            Text(challenge.tournamentName)
        }

        challenge.description?.let { desc ->
            P({
                style {
                    color(Colors.TextSecondary)
                    fontSize(14.px)
                    marginBottom(16.px)
                }
            }) {
                Text(desc)
            }
        }

        // Draw info
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(16.px)
                flexWrap(FlexWrap.Wrap)
                marginBottom(24.px)
            }
        }) {
            InfoChip("Draw Size", "${draw.drawSize} players")
            InfoChip("Gender", if (draw.isMens) "Men's" else "Women's")
            draw.surface?.let { InfoChip("Surface", it.replaceFirstChar { c -> c.uppercase() }) }
            InfoChip("Best Of", "${draw.bestOf} sets")
        }

        // Entry/Prize info grid
        Div({
            classes("challenge-stats-grid")
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(150px, 1fr))")
                gap(20.px)
            }
        }) {
            InfoBox("Entry Fee", challenge.entryFeeDisplay)
            InfoBox("Prize Pool", challenge.prizePoolDisplay)
            InfoBox("Entries", "${challenge.currentEntries} / ${challenge.maxEntries?.takeIf { it > 0 } ?: "Unlimited"}")
            InfoBox("Max Per User", "${challenge.maxEntriesPerUser} entries")
        }

        // Lock time display
        LockTimeDisplay(challenge)
    }
}

@Composable
private fun StatusBadge(status: BracketChallengeStatus) {
    val (bgColor, textColor, label) = when (status) {
        BracketChallengeStatus.UPCOMING -> Triple(Colors.BracketStatus.UpcomingBackground, Colors.BracketStatus.UpcomingText, "Upcoming")
        BracketChallengeStatus.LIVE -> Triple(Colors.BracketStatus.LiveBackground, Colors.BracketStatus.LiveText, "Live")
        BracketChallengeStatus.COMPLETED -> Triple(Colors.BracketStatus.CompletedBackground, Colors.BracketStatus.CompletedText, "Completed")
        BracketChallengeStatus.CANCELLED -> Triple(Colors.BracketStatus.CancelledBackground, Colors.BracketStatus.CancelledText, "Cancelled")
        BracketChallengeStatus.PAYING_OUT -> Triple(Colors.BracketStatus.PayingOutBackground, Colors.BracketStatus.PayingOutText, "Paying Out")
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(12.px)
            fontWeight(600)
            padding(4.px, 12.px)
            borderRadius(4.px)
        }
    }) {
        Text(label)
    }
}

@Composable
private fun InfoChip(label: String, value: String) {
    Span({
        style {
            backgroundColor(Colors.SurfaceVariant)
            padding(6.px, 12.px)
            borderRadius(16.px)
            fontSize(13.px)
        }
    }) {
        Span({
            style {
                color(Colors.TextSecondary)
            }
        }) {
            Text("$label: ")
        }
        Span({
            style {
                fontWeight(500)
                color(Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun InfoBox(label: String, value: String) {
    Div({
        classes("challenge-info-box")
        style {
            backgroundColor(Colors.Surface)
            border(1.px, LineStyle.Solid, Colors.BorderLight)
            padding(20.px, 16.px)
            borderRadius(12.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(6.px)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.5px")
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(24.px)
                fontWeight(700)
                color(Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun LockTimeDisplay(challenge: BracketChallengeEntity) {
    val isLocked = challenge.isLocked
    val timeUntilLock = challenge.timeUntilLock

    Div({
        style {
            marginTop(16.px)
            padding(12.px, 16.px)
            borderRadius(8.px)
            backgroundColor(if (isLocked) Colors.ErrorLight else Colors.SuccessLight)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        Span({
            style {
                fontSize(16.px)
            }
        }) {
            Text(if (isLocked) "\uD83D\uDD12" else "\u23F0") // Lock or alarm clock emoji
        }
        Span({
            style {
                fontSize(14.px)
                fontWeight(500)
                color(Colors.TextPrimary)
            }
        }) {
            val text = when {
                isLocked -> "Entries are locked"
                challenge.isManuallyUnlocked -> "Entries open (extended by admin)"
                timeUntilLock != null -> "Entries lock in: ${formatCountdown(timeUntilLock)}"
                else -> "Lock time: ${formatLockTime(challenge.lockTime)}"
            }
            Text(text)
        }
    }
}

private fun formatLockTime(isoTimestamp: String): String {
    return try {
        val jsDate = Date(isoTimestamp)
        jsDate.toLocaleString()
    } catch (e: Exception) {
        isoTimestamp
    }
}

private fun formatCountdown(duration: Duration): String {
    val days = duration.inWholeDays
    val hours = duration.inWholeHours % 24
    val minutes = duration.inWholeMinutes % 60
    return when {
        days > 0 -> "${days}d ${hours}h ${minutes}m"
        hours > 0 -> "${hours}h ${minutes}m"
        else -> "${minutes}m"
    }
}

@Composable
private fun UserEntriesSection(
    entries: List<BracketEntryEntity>,
    challengeId: String
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                margin(0.px)
                marginBottom(16.px)
            }
        }) {
            Text("Your Entries")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            entries.forEach { entry ->
                EntryRow(entry, challengeId)
            }
        }
    }
}

@Composable
private fun EntryRow(entry: BracketEntryEntity, challengeId: String) {
    // Status color for indicator dot
    val statusColor = when (entry.status) {
        BracketEntryStatus.PENDING -> Colors.Warning
        BracketEntryStatus.SUBMITTED -> Colors.Success
        BracketEntryStatus.LIVE -> Colors.Primary
        BracketEntryStatus.FINISHED -> Colors.Gray500
        BracketEntryStatus.PAID -> Colors.Success
        BracketEntryStatus.CANCELLED -> Colors.Error
    }

    Div({
        classes("challenge-entry-row")
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(16.px, 20.px)
            backgroundColor(Colors.Surface)
            border(1.px, LineStyle.Solid, Colors.BorderLight)
            borderRadius(10.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            // Status indicator dot
            Div({
                style {
                    width(10.px)
                    height(10.px)
                    borderRadius(50.percent)
                    backgroundColor(statusColor)
                    flexShrink(0)
                }
            })
            Div {
                Div({
                    style {
                        fontWeight(600)
                        fontSize(15.px)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text(entry.displayName)
                }
                Div({
                    style {
                        fontSize(13.px)
                        color(Colors.TextSecondary)
                        marginTop(2.px)
                    }
                }) {
                    val statusText = when (entry.status) {
                        BracketEntryStatus.PENDING -> "Draft - ${entry.pendingPicks} picks remaining"
                        BracketEntryStatus.SUBMITTED -> "Submitted"
                        BracketEntryStatus.LIVE -> "${entry.totalPoints.toInt()} pts - Rank #${entry.rank ?: "-"}"
                        BracketEntryStatus.FINISHED -> "${entry.totalPoints.toInt()} pts - Final Rank #${entry.rank ?: "-"}"
                        BracketEntryStatus.PAID -> "Won ${entry.payoutAmount / 100.0}!"
                        BracketEntryStatus.CANCELLED -> "Cancelled"
                    }
                    Text(statusText)
                }
            }
        }

        Button(
            text = when (entry.status) {
                BracketEntryStatus.PENDING -> "Edit Bracket"
                BracketEntryStatus.SUBMITTED -> "View Bracket"
                else -> "View"
            },
            onClick = {
                NavigationState.navigate(Route.BracketBuilder(challengeId, entry.id))
            },
            variant = "secondary"
        )
    }
}

@Composable
private fun EnterChallengeSection(
    challenge: BracketChallengeEntity,
    isEntering: Boolean,
    onEnter: () -> Unit
) {
    Div({
        style {
            property("background", "linear-gradient(135deg, rgb(232, 245, 233) 0%, rgb(200, 230, 201) 100%)")
            border(2.px, LineStyle.Solid, Colors.Primary)
            borderRadius(16.px)
            padding(32.px)
            marginBottom(24.px)
            textAlign("center")
        }
    }) {
        H3({
            style {
                fontSize(24.px)
                fontWeight(700)
                color(Colors.TextPrimary)
                margin(0.px)
                marginBottom(8.px)
            }
        }) {
            Text("Ready to compete?")
        }
        P({
            style {
                color(Colors.TextSecondary)
                fontSize(15.px)
                marginBottom(20.px)
            }
        }) {
            Text("Entry fee: ${challenge.entryFeeDisplay}")
        }
        // Larger CTA button
        Div({
            style {
                display(DisplayStyle.InlineBlock)
            }
        }) {
            org.jetbrains.compose.web.dom.Button({
                classes("baseline-button", "baseline-button--primary")
                onClick { if (!isEntering) onEnter() }
                if (isEntering) attr("disabled", "true")
                style {
                    backgroundColor(Colors.Primary)
                    color(Colors.White)
                    border(0.px, LineStyle.Solid, Colors.Primary)
                    borderRadius(10.px)
                    padding(14.px, 40.px)
                    fontSize(18.px)
                    fontWeight(600)
                    cursor(if (isEntering) "not-allowed" else "pointer")
                    property("transition", "all 0.2s")
                    property("box-shadow", "0 4px 12px rgba(0, 166, 81, 0.3)")
                }
            }) {
                Text(if (isEntering) "Creating entry..." else "Enter Challenge")
            }
        }
    }
}

@Composable
private fun ScoringRulesSection(
    rules: ScoringRulesEntity,
    drawSize: Int
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                margin(0.px)
                marginBottom(16.px)
            }
        }) {
            Text("Scoring Rules")
        }

        // Points per round table
        Table({
            style {
                width(100.percent)
                property("border-collapse", "collapse")
            }
        }) {
            Thead {
                Tr({
                    style {
                        backgroundColor(Colors.SurfaceVariant)
                    }
                }) {
                    Th({
                        style {
                            textAlign("left")
                            padding(12.px, 16.px)
                            color(Colors.TextSecondary)
                            fontSize(11.px)
                            property("text-transform", "uppercase")
                            property("letter-spacing", "0.5px")
                            fontWeight(600)
                        }
                    }) {
                        Text("Round")
                    }
                    Th({
                        style {
                            textAlign("right")
                            padding(12.px, 16.px)
                            color(Colors.TextSecondary)
                            fontSize(11.px)
                            property("text-transform", "uppercase")
                            property("letter-spacing", "0.5px")
                            fontWeight(600)
                        }
                    }) {
                        Text("Points")
                    }
                }
            }
            Tbody {
                val rounds = RoundName.getRoundsForDrawSize(drawSize)

                rounds.forEachIndexed { index, round ->
                    Tr({
                        classes("scoring-table-row")
                        style {
                            if (index % 2 == 1) {
                                backgroundColor(rgba(0, 0, 0, 0.02))
                            }
                        }
                    }) {
                        Td({
                            style {
                                padding(14.px, 16.px)
                                borderBottom(1.px, LineStyle.Solid, Colors.BorderLight)
                                color(Colors.TextPrimary)
                            }
                        }) {
                            Text(round.displayName)
                        }
                        Td({
                            style {
                                textAlign("right")
                                padding(14.px, 16.px)
                                borderBottom(1.px, LineStyle.Solid, Colors.BorderLight)
                                fontWeight(600)
                                color(Colors.Primary)
                            }
                        }) {
                            Text("${rules.getPointsForRound(round)}")
                        }
                    }
                }
            }
        }

        // Bonus rules
        if (rules.upsetBonusEnabled) {
            Div({
                style {
                    marginTop(16.px)
                    padding(12.px, 16.px)
                    backgroundColor(Colors.WarningLight)
                    borderRadius(8.px)
                    fontSize(13.px)
                }
            }) {
                B { Text("Upset Bonus: ") }
                Text("+${(rules.upsetBonusMultiplier * 100).toInt()}% bonus when picking an upset winner")
            }
        }
    }
}

@Composable
private fun PayoutStructureSection(
    payouts: List<PayoutTierEntity>,
    prizePool: Int
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                margin(0.px)
                marginBottom(16.px)
            }
        }) {
            Text("Payout Structure")
        }

        Table({
            style {
                width(100.percent)
                property("border-collapse", "collapse")
            }
        }) {
            Thead {
                Tr({
                    style {
                        backgroundColor(Colors.SurfaceVariant)
                    }
                }) {
                    Th({
                        style {
                            textAlign("left")
                            padding(12.px, 16.px)
                            color(Colors.TextSecondary)
                            fontSize(11.px)
                            property("text-transform", "uppercase")
                            property("letter-spacing", "0.5px")
                            fontWeight(600)
                        }
                    }) {
                        Text("Place")
                    }
                    Th({
                        style {
                            textAlign("right")
                            padding(12.px, 16.px)
                            color(Colors.TextSecondary)
                            fontSize(11.px)
                            property("text-transform", "uppercase")
                            property("letter-spacing", "0.5px")
                            fontWeight(600)
                        }
                    }) {
                        Text("Prize")
                    }
                }
            }
            Tbody {
                payouts.forEachIndexed { index, tier ->
                    Tr({
                        classes("scoring-table-row")
                        style {
                            if (index % 2 == 1) {
                                backgroundColor(rgba(0, 0, 0, 0.02))
                            }
                        }
                    }) {
                        Td({
                            style {
                                padding(14.px, 16.px)
                                borderBottom(1.px, LineStyle.Solid, Colors.BorderLight)
                                color(Colors.TextPrimary)
                            }
                        }) {
                            // Medal icons for top 3
                            val medal = when (tier.minRank) {
                                1 -> "\uD83E\uDD47 " // Gold medal
                                2 -> "\uD83E\uDD48 " // Silver medal
                                3 -> "\uD83E\uDD49 " // Bronze medal
                                else -> ""
                            }
                            val placeText = if (tier.minRank == tier.maxRank) {
                                "${medal}#${tier.minRank}"
                            } else {
                                "#${tier.minRank} - #${tier.maxRank}"
                            }
                            Text(placeText)
                        }
                        Td({
                            style {
                                textAlign("right")
                                padding(14.px, 16.px)
                                borderBottom(1.px, LineStyle.Solid, Colors.BorderLight)
                                fontWeight(600)
                                color(Colors.Primary)
                            }
                        }) {
                            val amount = tier.amount
                                ?: (tier.percentage?.let { (it * prizePool / 100).toInt() })
                                ?: 0
                            val formattedAmount = (amount / 100.0).asDynamic().toFixed(2) as String
                            Text("$$formattedAmount")
                        }
                    }
                }
            }
        }
    }
}
